import withStore, {
  TPresentable,
  TLoadingAwarePresenter
} from "../../core/presenter/withStore";
import Application from "../../Application";
import { ISearchResponse, TSearchResult } from "../model/SearchResponse";
import { IOrganisation } from "../../organisations/model/Organisation";
import { TSearchQueryRequest } from "../service/SearchService";
import { IOrganisationIndexGroup } from "../../organisations/model/OrganisationIndexGroup";
import OrganisationIndexTreePresentationModel, {
  TOrganisationIndexTreeItem
} from "../../organisations/presenter/model/OrganisationIndexTreePresentationModel";
import { TDocumentType } from "../../documentTypes/model/DocumentType";
import { IUser } from "../../user/model/User";
import ShowSearchResultsInteractor from "../interactor/ShowSearchResultsInteractor";
import { TSearchCriteriaFilter } from "../model/SearchCriteria";
import SearchQueryPresentationModel, {
  TSearchQueryPresentationModel
} from "../model/SearchQueryPresentationModel";
import { IGenericCodebookItem } from "../../codebooks/model/GenericCodebookItem";
import { DataGridConstants } from "../../core/common/Constants";

export interface TSearchResultsPresenter extends TLoadingAwarePresenter {
  canCreateDocuments: boolean;
  searchQuery?: TSearchQueryRequest;
  searchResults?: ISearchResponse;
  organisations?: IOrganisation[];
  caseLawDocumentTypes?: TDocumentType[];
  sortFields: string[];
  indexTrees?: TOrganisationIndexTreeItem[][];
  currentLanguage?: string;
  organisationIndexTree?: any[];
  user?: IUser;
  enrichedSearchQuery?: TSearchQueryPresentationModel;
  courtCaseTypes?: IGenericCodebookItem[];
  caseDecisionTypes?: IGenericCodebookItem[];
}

export interface ISearchResultsPresenter
  extends TSearchResultsPresenter,
    TPresentable {
  loadSearchResults(query: TSearchQueryRequest, result: ISearchResponse): void;
  loadReferenceValues(
    organisations: IOrganisation[],
    indexTrees: IOrganisationIndexGroup[][],
    currentLanguage: string,
    courtCaseTypes: IGenericCodebookItem[],
    caseDecisionTypes: IGenericCodebookItem[]
  ): void;
  onSearch(query: TSearchCriteriaFilter): any;
  createNewCaseLawDocument(): void;
  createNewCaseDecision(): void;
  createNewLegalView(): void;
  onSearchItemClick(item: TSearchResult): void;
  removeSelectedDate(property: string): void;
}

const defaultState: TSearchResultsPresenter = {
  sortFields: [],
  canCreateDocuments: false
};

const SearchResultsPresenter = withStore<
  ISearchResultsPresenter,
  TSearchResultsPresenter
>(({ application, store, loader, translate }): ISearchResultsPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<
    TSearchResultsPresenter & TLoadingAwarePresenter
  >();
  loader.start("search");
  loader.start("criteria");

  const loadSearchResults = (
    query: TSearchQueryRequest,
    result: ISearchResponse
  ) => {
    loader.stop("search");
    const { lastVisitedItemId } = store.getState<any>();
    let mainElement = document.getElementsByClassName(
      "ant-layout-content layout-content-container"
    )[0];
    if (mainElement?.scrollTop !== 0) {
      document
        .getElementById("search_results")
        ?.scrollIntoView({ behavior: "smooth" });
    }
    _store.update({
      searchResults: result,
      searchQuery: query
    });
    if (lastVisitedItemId) {
      document
        ?.getElementById(`search-result-item-${lastVisitedItemId}`)
        ?.scrollIntoView();
      document
        ?.getElementById(`search-result-item-${lastVisitedItemId}`)
        ?.classList.add("highlight");
    }
    const user = _store.getState<any>().user;
    if (user) {
      _store.update({ user });
    }
  };

  const onSearch = async (query: TSearchCriteriaFilter, reset?: boolean) => {
    const { searchQuery } = _store.getState<TSearchResultsPresenter>();
    let _searchQuery = reset
      ? {
          phrase: query.phrase
        }
      : Object.assign(
          {},
          searchQuery,
          {
            page: 1,
            pageSize: DataGridConstants.PAGE_SIZE
          },
          query
        );
    let queryString = Object.keys(_searchQuery)
      .map((item) => `${item}=${_searchQuery[item] || ""}`)
      .join("&");
    _store.update({
      searchQuery: _searchQuery
    });
    return _application.navigator.push({
      pathname: "/search",
      search: `?${queryString}`
    });
  };

  const removeSelectedDate = (property: string) => {
    const { searchQuery } = _store.getState<TSearchResultsPresenter>();
    if (!searchQuery) {
      return;
    }
    delete searchQuery[property];
    _application.container
      .resolve<ShowSearchResultsInteractor>("showSearchResults")
      .execute(searchQuery);
  };

  const loadReferenceValues = (
    organisations: IOrganisation[],
    organisationIndexTree: IOrganisationIndexGroup[][],
    currentLanguage: string,
    courtCaseTypes: IGenericCodebookItem[],
    caseDecisionTypes: IGenericCodebookItem[]
  ) => {
    const { searchResults, searchQuery } =
      _store.getState<TSearchResultsPresenter>();
    const indexTrees = organisationIndexTree?.map((item) =>
      OrganisationIndexTreePresentationModel(
        item,
        searchResults?.facets?.["index_id_is"]
      )
    );
    let enrichedSearchQuery = SearchQueryPresentationModel({
      translate,
      organisationIndexTree,
      organisations,
      query: searchQuery,
      courtCaseTypes,
      caseDecisionTypes
    });
    _store.update({
      organisations,
      organisationIndexTree,
      indexTrees,
      currentLanguage,
      enrichedSearchQuery,
      courtCaseTypes,
      caseDecisionTypes
    });
    const user = _store.getState<any>().user;
    if (user) {
      _store.update({ user });
    }
    loader.stop("criteria");
  };

  const createNewCaseLawDocument = () => {
    return _application.navigator.push({
      pathname: `/case-law-documents/new`
    });
  };

  const createNewCaseDecision = () => {
    return _application.navigator.push({
      pathname: `/case-decisions/new`
    });
  };
  const createNewLegalView = () => {
    return _application.navigator.push({
      pathname: `/legal-views/new`
    });
  };

  const onSearchItemClick = (item: TSearchResult) => {
    console.log(item.id);
    _store.update({
      lastVisitedItemId: item.id
    });
  };
  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadSearchResults,
    removeSelectedDate,
    onSearch,
    createNewCaseLawDocument,
    createNewCaseDecision,
    createNewLegalView,
    onSearchItemClick,
    loadReferenceValues
  };
}, defaultState);

export default SearchResultsPresenter;
